<script setup lang="ts">
import ParagraphTemplate from '@/views/projects-template/ParagraphTemplate.vue'
import { docUrl } from '@/components/scenario-form/constants/constants'
import { useRouter } from 'vue-router'

declare global {
  interface Window {
    navigateToProjects: () => void
    navigateToAboutSpin: () => void
    navigateToProjectExamples: () => void
    navigateToDoc: () => void
    navigateToPrepareData: () => void
  }
}

const extraPath = 'data-preparation'

const router = useRouter()

function navigateToProjects() {
  router.push('/project-index')
}
function navigateToAboutSpin() {
  router.push('/about-spin')
}
function navigateToProjectExamples() {
  router.push('/project-examples')
}
function navigateToPrepareData() {
  const url = docUrl ? docUrl + extraPath : '/docs/index#data-preparation'
  window.open(url, '_blank')
}

window.navigateToProjects = navigateToProjects
window.navigateToAboutSpin = navigateToAboutSpin
window.navigateToProjectExamples = navigateToProjectExamples
window.navigateToDoc = navigateToPrepareData

const sections = [
  {
    title: 'Welcome to the SPIN tool!',
    content: `
      <p>
        In this section, you will find a short description to help navigate in the Spatial
        Inequality tool (SPIN). If you’d like to know more about the tool’s general application of
        the tool, please check
        <span class="text-primary" style="cursor: pointer" onclick="navigateToAboutSpin()">‘About SPIN’</span>
        tab or go to
        <span class="text-primary" style="cursor: pointer" onclick="navigateToProjectExamples()">‘Project examples’</span>
        where you will be guided through one of our projects, learning about the tool’s
        functionalities and practical application of each analysis.
        <span class="text-primary" style="cursor: pointer; display: inline-flex; align-items: center" onclick="navigateToDoc()">
          A separate manual was created that shows the user in a step-by-step approach how they
          can prepare the input data
          <span class="mdi mdi-open-in-new" style="margin-left: 3spx"></span>
        </span>
      </p>
    `
  },
  {
    title: 'My projects',
    content: `
      <p style="margin-bottom: 10px">
        Under the tab
        <span class="text-primary" style="cursor: pointer" onclick="navigateToProjects()">My Projects</span>,
        you can create projects and run simulations based on your own choices. By simulation we mean
        <strong>a singular analytical process undertaken by the tool, resulting in a set of outcome layers</strong>.
      </p>
      <ul style="padding-left: 20px; margin-bottom: 10px">
        <li>There is no limit to the number of simulations created within a project.</li>
        <li>
          Each simulation may have a different focus point, and its up to a user to define
          variables such as 1) population to be analyzed (e.g. an entire community or a specific
          gender group), 2) type of destination (e.g. a resource/ facility/ service/
          infrastructure), and optionally 3) type of disruption, if such occurs (e.g. flood).
        </li>
        <li>
          While the tool is designed to analyze how accessibility changes during different
          disastrous events, it can also be used to simulate business-as-usual scenarios to
          address the level of accessibility in general, for example to inform spatial planning
          activities.
        </li>
      </ul>
      <p style="margin-bottom: 10px; display: flex; justify-content: center; align-items: center">
        <img class="content-image" src="/MyProjectsGuide.png" alt="SPIN My Projects Image" />
      </p>
      <p style="margin-bottom: 10px">
        In this space, you can add, view and modify your projects. A project is a space where you
        can view, store and add new simulations. Please note that you can only see projects which
        you either created, or were granted access to co-create them by your colleagues.
      </p>
    `
  },
  {
    title: 'Setting the Scene',
    content: `
      <p style="margin-bottom: 10px">
        In the
        <span class="text-primary" style="cursor: pointer" onclick="navigateToProjects()">Setting the Scene</span>
        window, a user chooses parameters for a simulation. While the tool carries out spatial
        analyses and calculations, it does not provide datasets. Those need to be prepared in
        advance and provided by users themselves. There are guidelines for the data preparation
        under dedicated icons next to each item.
      </p>
      <p style="margin-bottom: 10px">
        In the selection window, a user is also asked to select a calculation method to assess the
        level of accessibility (based either on distance or time travel), but also to indicate the
        intended outcome(s) from the list of available analyses. Each item has a pop-up window
        containing additional information about the purpose of that analysis.
      </p>
      <p style="margin-bottom: 10px">A user is required to provide the following input data:</p>
      <ul style="padding-left: 20px; margin-bottom: 10px">
        <li>
          Networks - a layer containing road network which will be used to generate routes between
          each origin point (e.g. population) and the closest destination.
        </li>
        <li>
          Origins – a layer containing information about intended origins (e.g. a specific
          population group).
        </li>
        <li>
          Destinations – a layer containing information about points of interest (e.g. resource /
          facility / type of services etc.)
        </li>
        <li>Disruptions (optional)</li>
      </ul>
      <p style="margin-bottom: 10px; display: flex; justify-content: center; align-items: center">
        <img class="content-image" src="/SettingTheScene.png" alt="SPIN Setting The Scence Image" />
      </p>
    `
  },
  {
    title: 'Calculation method',
    content: `
      <p style="margin-bottom: 10px">
        In the selection window, a user is also asked to select a calculation method to assess the
        level of accessibility (based either on distance or travel time), but also to indicate the
        intended outcome(s) from the list of available analyses. Each item has a pop-up window
        containing additional information about the purpose of that analysis.
      </p>
      <p style="margin-bottom: 10px; display: flex; justify-content: center; align-items: center">
        <img class="content-image" src="/CalculationMethod.png" alt="SPIN Calculation Method Image" />
      </p>
    `
  },
  {
    title: 'Viewer',
    content: `
      <p style="margin-bottom: 10px">
        In the last step, a user is brought to a "Viewer" page, where they are able to view the
        results of a simulation of interest, or compare two different simulations by placing them
        next to each other
      </p>
      <p style="margin-bottom: 10px">
        Result seen in the viewer are interactive. After clicking on individual elements on the
        map, users can access additional information regarding specific values of the number of
        people affected.
      </p>
      <p style="margin-bottom: 10px">
        On that page, the output data can be downloaded in a form of GIS layers or exported as
        maps into a PDF format.
      </p>
    `
  }
]
</script>

<template>
  <ParagraphTemplate title="How to get started" :sections="sections" />
</template>

<style scoped></style>
